import { Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, InputAdornment, MenuItem, Select, TextField, Typography, FormControlLabel, Checkbox, FormGroup } from '@mui/material'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import LastUpdatedComponent from 'components/common/format-date'
import { SortableList } from 'components/common/drag-and-drop/sortable-list'
import React, { useEffect, useState } from 'react'
import {
  fillMember,
  type MemberInformation, type MemberRankInformation, type MemberStaffInformation, retireMember,
  updateMemberInfo
} from 'services/person/us/edit/memberInfoService'
import { type PersonBaseBasicInformation } from 'services'
import { PersonAutocomplete, useSeatStatusLuts } from 'components/international'
import { CommentDialog } from '../common/comment-dialog'
import { formatErrorFieldMessages } from 'interfaces'
import { AlertDialog, EditActions, IconButtonWithTooltip, useGlobalMessageHandler, useUsPrefixLuts } from 'components'
import {
  validateDates,
  validateSeatStatus,
  validateNumericFields
} from './validations/memberValidations'
import { MajMinAffilication, ElectCode } from 'utils'
import { formatFullName, generateLegalName, isFieldEditableForVacant, SeatStatusConstants } from 'helpers'

const formatRatingValue = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return ''
  return value
}

export interface MemberCardProps {
  index: number
  originalPosition: MemberInformation
  person: PersonBaseBasicInformation
  createCard: boolean
  loading: boolean
  onRankingUpdated: (updatedMembers: MemberRankInformation[]) => Promise<void>
  onFetchChamberRankings: () => Promise<MemberRankInformation[]>
  onPositionUpdate?: (updatedPosition: MemberInformation) => void
  staff?: MemberStaffInformation[]
  redirectToMemberPage: (personId: number) => void
  onDirtyChange?: (dirty: boolean) => void
  saveTrigger?: number
}

export const MemberCard: React.FC<MemberCardProps> = ({
  originalPosition,
  person: _person,
  index,
  createCard,
  loading,
  onRankingUpdated,
  onFetchChamberRankings,
  onPositionUpdate,
  staff = [],
  redirectToMemberPage,
  onDirtyChange,
  saveTrigger
}) => {
  const [position, setPosition] = useState<MemberInformation>(originalPosition)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [editingCard, setEditingCard] = useState(createCard)
  const [rankModalOpen, setRankModalOpen] = useState(false)
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [updatingMembers, setUpdatingMembers] = useState(false)
  const [originalMembers, setOriginalMembers] = useState<MemberRankInformation[]>([])
  const [updatedMembers, setUpdatedMembers] = useState<MemberRankInformation[]>([])
  const isVacant = originalPosition.person.lastName === 'Vacant'
  const { lutValues: seatStatusLutValues, loading: loadingSeatStatusLutValues } = useSeatStatusLuts()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()

  const handleRankModalOpen = async (): Promise<void> => {
    setRankModalOpen(true)
    setLoadingMembers(true)

    try {
      const members = await onFetchChamberRankings()
      setOriginalMembers(members)
      setUpdatedMembers(members)
    } catch (error) {
      console.error('Failed to fetch chamber members')
    } finally {
      setLoadingMembers(false)
    }
  }

  useEffect(() => {
    if (!loadingMembers && rankModalOpen && position?.id != null) {
      setTimeout(() => {
        const currentMemberElement = document.getElementById(`member-rank-${position.id}`)
        if (currentMemberElement != null) {
          currentMemberElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      }, 500)
    }
  }, [loadingMembers, rankModalOpen, position?.id])

  const handleRankModalSave = async (): Promise<void> => {
    setUpdatingMembers(true)
    try {
      await onRankingUpdated(updatedMembers)
      setRankModalOpen(false)
      showSuccessMessage('Member rankings updated successfully')
    } catch (error: any) {
      showErrorMessage('Failed to update member rankings')
    } finally {
      setUpdatingMembers(false)
    }
  }

  const handleRankModalCancel = (): void => {
    setRankModalOpen(false)
    setUpdatedMembers(originalMembers)
  }

  useEffect(() => {
    setPosition(originalPosition)
  }, [originalPosition])

  const handleClearSelection = (): void => {
    setPosition(originalPosition)
    setDisabled(true)
    setEditingCard(false)
    onDirtyChange?.(false)
  }

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const newValue = event.target.value
    setPosition((prevPosition) => ({
      ...prevPosition,
      [event.target.name]: newValue
    }))
    onDirtyChange?.(true)
  }

  const handleSeatStatusChange = (newValue: number): void => {
    setPosition((prevPosition) => {
      const newStatus = seatStatusLutValues.statuses.find((status) => status.id === newValue)
      if (newStatus != null) {
        return {
          ...prevPosition,
          legseat: {
            ...prevPosition.legseat,
            status: newStatus
          }
        }
      }
      return prevPosition
    })
    onDirtyChange?.(true)
  }

  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const [loadingClick, setLoadingClick] = useState(false)
  const [openFillDialog, setOpenFillDialog] = useState(false)
  const [openRetireDialog, setOpenRetireDialog] = useState(false)

  const [isCreatingPerson, setIsCreatingPerson] = useState(false)
  const [isEditingPerson, setIsEditingPerson] = useState(true)
  const [selectedStaff, setSelectedStaff] = useState<number[]>([])
  const [loadingFillPosition, setLoadingFillPosition] = useState(false)

  const handleFill = (): void => {
    setOpenFillDialog(true)
    setIsEditingPerson(true)
    setIsCreatingPerson(false)
  }
  const handleRetire = (): void => {
    setOpenRetireDialog(true)
  }
  const handleConfirmedRetirement = async (): Promise<void> => {
    try {
      setLoadingClick(true)
      const response = await retireMember(position.id)
      if (response.succeeded && response.result != null) {
        showSuccessMessage('Member retired')
        redirectToMemberPage(response.result.vacantPersonId)
      }
    } catch (error: any) {
      showErrorMessage(formatErrorFieldMessages(error.response.data.errors))
    } finally {
      setLoadingClick(false)
    }
  }

  const handlePersonSelected = async (person: PersonBaseBasicInformation): Promise<void> => {
    if (person == null || (person.firstName == null && person.lastName == null)) return

    setLoadingFillPosition(true)
    setIsEditingPerson(true)
    setIsCreatingPerson(false)

    try {
      const response = await fillMember(position.id, selectedStaff, person)
      if (response.result != null && response.succeeded) {
        showSuccessMessage('Member position filled')
        setOpenFillDialog(false)
        redirectToMemberPage(response.result.occupantPersonId)
      }
    } catch (error: any) {
      showErrorMessage(formatErrorFieldMessages(error.response.data.errors))
    } finally {
      setLoadingFillPosition(false)
    }
  }

  const savePosition = async (): Promise<void> => {
    clearMessages()
    setLoadingClick(true)

    try {
      const validations = [
        validateDates(position),
        validateSeatStatus(position),
        validateNumericFields(position)
      ]

      const failedValidation = validations.find(v => !v.isValid)
      if (failedValidation !== null && failedValidation !== undefined) {
        showErrorMessage(failedValidation.message ?? 'Validation failed')
        setLoadingClick(false)
        return
      }

      if (position.fecLinkNum === '') {
        position.fecLinkNum = null
      }

      const response = await updateMemberInfo(position.id, position)

      if (response?.result != null) {
        setPosition(response.result)
        onPositionUpdate?.(response.result)
        showSuccessMessage('Position updated successfully')
        setDisabled(true)
        setEditingCard(false)
        onDirtyChange?.(false)
      } else {
        let errorMessage = 'Failed to update position'

        if (response !== null && response !== undefined) {
          if (response.errors !== null && response.errors !== undefined && typeof response.errors === 'object') {
            const errorValues = Object.values(response.errors)
            if (errorValues.length > 0 && errorValues[0] !== null && errorValues[0] !== undefined) {
              errorMessage = errorValues[0].toString()
            }
          } else if (response.message !== null && response.message !== undefined) {
            if (typeof response.message === 'object') {
              const messageValues = Object.values(response.message)
              if (messageValues.length > 0 && messageValues[0] !== null && messageValues[0] !== undefined) {
                errorMessage = typeof messageValues[0] === 'object'
                  ? JSON.stringify(messageValues[0])
                  : String(messageValues[0])
              }
            } else if (typeof response.message === 'string') {
              errorMessage = response.message
            }
          }
        }
        showErrorMessage(errorMessage)
      }
    } catch (error: any) {
      let errorMessage = 'An error occurred while saving'

      if (error !== null && error !== undefined) {
        if (error.response?.data != null) {
          const response = error.response.data

          if (response.message !== null && response.message !== undefined) {
            if (typeof response.message === 'object') {
              const messageValues = Object.values(response.message)
              if (messageValues.length > 0 && messageValues[0] !== null && messageValues[0] !== undefined) {
                errorMessage = typeof messageValues[0] === 'object'
                  ? JSON.stringify(messageValues[0])
                  : String(messageValues[0])
              }
            } else if (typeof response.message === 'string') {
              errorMessage = response.message
            }
          } else if (response.errors !== null && response.errors !== undefined &&
                     typeof response.errors === 'object') {
            const errorValues = Object.values(response.errors)
            if (errorValues.length > 0 && errorValues[0] !== null && errorValues[0] !== undefined) {
              errorMessage = typeof errorValues[0] === 'object'
                ? JSON.stringify(errorValues[0])
                : String(errorValues[0])
            }
          }
        } else if (error.message !== null && error.message !== undefined) {
          errorMessage = error.message
        }
      }
      showErrorMessage(errorMessage)
    } finally {
      setLoadingClick(false)
    }
  }

  useEffect(() => {
    if (saveTrigger != null && saveTrigger > 0) {
      void savePosition()
    }
  }, [saveTrigger])

  const isDisabledFields = (): boolean => {
    return disabled && !editingCard
  }

  const isDisabledFieldsForVacant = (fieldName?: string): boolean => {
    const baseDisabled = disabled && !editingCard

    if (!isVacant) {
      return baseDisabled
    }

    if ((fieldName != null) && isFieldEditableForVacant(fieldName)) {
      return baseDisabled
    }

    return true
  }

  const getCardText = (member: MemberInformation): string => {
    if (member.person.lastName === SeatStatusConstants.VACANT_PERSON_LASTNAME) {
      return 'Vacant'
    }

    const districtCode = member.legseat.legdistrict.code
    const firstName = member.person.firstName
    const lastName = member.person.lastName

    if ((lastName == null) || (lastName === '') || (firstName == null) || (firstName === '') || (districtCode === '')) {
      return 'Invalid member data'
    }

    const fullName = formatFullName(member.person, usPrefixLutValues.prefixes)
    return `${fullName} (${districtCode})`
  }

  const getEnhancedMajMinText = (majminValue: string): string => {
    if (majminValue === '') {
      return ''
    }

    const baseText = MajMinAffilication[majminValue as keyof typeof MajMinAffilication] ?? majminValue
    const enumKeys = Object.keys(MajMinAffilication) as Array<keyof typeof MajMinAffilication>
    const [MIN, MAJ, OTH] = enumKeys

    if (majminValue === OTH) {
      return baseText
    }

    if (majminValue === MAJ && (position.chamber_majority_party != null)) {
      const desc = position.chamber_majority_party.desc ?? ''
      const code = position.chamber_majority_party.code ?? ''
      return `${baseText} - ${desc} (${code})`
    }
    if (majminValue === MIN && (position.chamber_minority_party != null)) {
      const desc = position.chamber_minority_party.desc ?? ''
      const code = position.chamber_minority_party.code ?? ''
      return `${baseText} - ${desc} (${code})`
    }

    return baseText
  }

  return (
    <>
      <Card key={`position-card-${index}`} sx={{ padding: 2 }}>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Box pb={2}><LastUpdatedComponent value={position.lastUpdated}></LastUpdatedComponent></Box>
        </Box>
        <Grid container alignItems={'center'} spacing={1}>
          <Grid item xs={2}>
            <FormControl fullWidth>
              <InputLabel id={`selectStatusType-${position.id}`}>Seat Status</InputLabel>
              <Select disabled={isDisabledFieldsForVacant('seatStatus') || loadingSeatStatusLutValues} onChange={(event) => { handleSeatStatusChange(Number(event.target.value)) }} value={position.legseat?.status.id} labelId={`selectStatusType-${position.id}`}
                      label={'Seat Status'}>
                {
                  seatStatusLutValues.statuses.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                          {option.statusCode} - {option.statusDesc}
                      </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField
                name={'chamberRank'}
                disabled={true}
                label={'Rank'}
                value={position.chamberRank}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButtonWithTooltip
                        color="primary"
                        label="View Rank Information"
                        onClick={() => { void handleRankModalOpen() }}
                        disabled={isDisabledFields()}
                        icon={<OpenInNewIcon />}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField name={'electDate'} disabled={isDisabledFieldsForVacant('electDate')} label={'First Elect'} value={position.electDate} onChange={handleFieldChange} />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField name={'termNumber'} disabled={isDisabledFieldsForVacant('termNumber')} label={'Term'} value={position.termNumber} onChange={handleFieldChange} />
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <FormControl fullWidth>
              <InputLabel id={`selectElectType-${position.id}`}>Elect Type</InputLabel>
              <Select
                label={'Elect Type'}
                disabled={isDisabledFieldsForVacant('electCode')}
                onChange={(event) => {
                  setPosition(prev => ({
                    ...prev,
                    electCode: event.target.value
                  }))
                }}
                value={position.electCode}
                labelId={`selectElectType-${position.id}`}
              >
                {
                  Object.entries(ElectCode).map((entry) => (
                      <MenuItem key={`elect-code-${entry[0]}`} value={entry[0]}>
                          { entry[1] }
                      </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField name={'percentVote'} disabled={isDisabledFieldsForVacant('percentVote')} label={'Last Vote %'} value={position.percentVote} onChange={handleFieldChange} />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'reelectYear'} disabled={isDisabledFieldsForVacant('reelectYear')} label={'Next Elect'} value={position.reelectYear} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'fecLinkNum'} disabled={isDisabledFieldsForVacant('fecLinkNum')} label={'FEC Link #'} value={position.fecLinkNum} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <FormControl fullWidth>
              <InputLabel id={`selectAffiliationType-${position.id}`}>Majority / Minority Affiliation</InputLabel>
              <Select
                label={'Majority / Minority Affiliation'}
                disabled={isDisabledFieldsForVacant('majmin')}
                onChange={(event) => {
                  setPosition(prev => ({
                    ...prev,
                    majmin: event.target.value
                  }))
                }}
                value={position.majmin}
                labelId={`selectAffiliation-${position.id}`}
              >
                {
                  Object.entries(MajMinAffilication).map((entry) => (
                      <MenuItem key={`elect-code-${entry[0]}`} value={entry[0]}>
                          { getEnhancedMajMinText(entry[0]) }
                      </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'pyracusc'} disabled={isDisabledFieldsForVacant('pyracusc')} label={'PYRACUSC'} value={formatRatingValue(position.pyracusc)} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'cyracusc'} disabled={isDisabledFieldsForVacant('cyracusc')} label={'CYRACUSC'} value={formatRatingValue(position.cyracusc)} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'pyradasc'} disabled={isDisabledFieldsForVacant('pyradasc')} label={'PYRADASC'} value={formatRatingValue(position.pyradasc)} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'cyradasc'} disabled={isDisabledFieldsForVacant('cyradasc')} label={'CYRADASC'} value={formatRatingValue(position.cyradasc)} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'pyraflsc'} disabled={isDisabledFieldsForVacant('pyraflsc')} label={'PYRAFLSC'} value={formatRatingValue(position.pyraflsc)} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'cyraflsc'} disabled={isDisabledFieldsForVacant('cyraflsc')} label={'CYRAFLSC'} value={formatRatingValue(position.cyraflsc)} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'pyruscosc'} disabled={isDisabledFieldsForVacant('pyruscosc')} label={'PYRUSCOSC'} value={formatRatingValue(position.pyruscosc)} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
                <TextField name={'cyruscosc'} disabled={isDisabledFieldsForVacant('cyruscosc')} label={'CYRUSCOSC'} value={formatRatingValue(position.cyruscosc)} onChange={handleFieldChange}/>
            </FormControl>
          </Grid>
          {!loadingClick && (
            <Grid item xs={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <EditActions
                  entityName={isVacant ? 'Vacant Member' : 'Member Position'}
                  editing={!isDisabledFields()}
                  onEdit={() => { setDisabled(false) }}
                  onCancel={() => { handleClearSelection() }}
                  onSave={() => { void savePosition() }}
                />
                {isDisabledFields() && isVacant && (
                  <IconButtonWithTooltip label={'Fill Member Position'} color={'primary'} icon={<PersonAddIcon/>} onClick={handleFill}/>
                )}
                <CommentDialog recordId={originalPosition.id} recordType={'M'} recordStateId={originalPosition.legseat?.legdistrict?.state?.id ?? 0}/>
                {isDisabledFields() && (
                  <IconButtonWithTooltip label={'Retire Member'} color={'secondary'} icon={<HourglassEmptyIcon/>} onClick={handleRetire} disabled={isVacant}/>
                )}
              </Box>
            </Grid>
          )}
          {loadingClick && (
            <Grid item xs={1}>
              <CircularProgress />
            </Grid>
          )}
        </Grid>
        <Dialog
          open={rankModalOpen}
          onClose={() => {
            if (!updatingMembers) {
              handleRankModalCancel()
            }
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Chamber Rank</DialogTitle>
          <DialogContent>
            {(loadingMembers || updatingMembers)
              ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', flexDirection: 'column', gap: 2 }}>
                  <CircularProgress />
                  <Typography>{loadingMembers ? 'Loading members...' : 'Saving changes...'}</Typography>
                </Box>
                )
              : (
                <SortableList<MemberRankInformation>
                  title="Chamber Members"
                  items={updatedMembers}
                  id={`chamber-members-${position.legseat?.legdistrict?.state?.desc ?? 'unknown'}`}
                  loading={loading}
                  getCardText={getCardText}
                  itemIdPrefix="member-rank"
                  isItemDisabled={(_item) => isVacant}
                  deferUpdates={true}
                  onItemsChange={setUpdatedMembers}
                />
                )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleRankModalCancel}
              disabled={updatingMembers}
              startIcon={<CloseIcon />}
            >
              Discard Changes
            </Button>
            <Button
              onClick={() => { handleRankModalSave().catch(console.error) }}
              disabled={updatingMembers}
              variant="contained"
              startIcon={<SaveIcon />}
            >
              {updatingMembers ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
      <Dialog
        open={openFillDialog}
        onClose={() => {
          if (!loadingFillPosition) {
            setOpenFillDialog(false)
            setIsEditingPerson(true)
            setIsCreatingPerson(false)
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Person to Fill Position</DialogTitle>
        <DialogContent>
          {loadingFillPosition
            ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
                py: 4
              }}>
                <CircularProgress size={50} sx={{ mb: 3 }} />
                <Typography>
                  Creating new person and filling position
                </Typography>
              </Box>
              )
            : (
              <Grid container spacing={2} sx={{ pt: 2 }}>
              <Grid item xs={12}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12}>
                    <PersonAutocomplete<PersonBaseBasicInformation>
                      person={{
                        id: 0, // 0 for new person
                        firstName: '',
                        lastName: '',
                        prefixId: undefined,
                        suffixId: undefined,
                        profSuffix: undefined,
                        legalName: undefined,
                        nickname: undefined
                      }}
                      updatePerson={ (person) => {
                        const completePersonData = {
                          ...person,
                          id: person.id ?? null,
                          prefixId: null,
                          suffixId: null,
                          profSuffix: null,
                          legalName: generateLegalName(person, []) !== '' ? generateLegalName(person, []) : null,
                          nickname: null
                        }
                        void handlePersonSelected(completePersonData as unknown as PersonBaseBasicInformation)
                      }}
                      filledPerson={false}
                      editable={true}
                      editing={isEditingPerson}
                      setEditing={(editing) => {
                        setIsEditingPerson(editing)
                      }}
                      createPerson={isCreatingPerson}
                      setCreatePerson={(creating) => {
                        setIsCreatingPerson(creating)
                      }}
                      personType={'personUS'}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Select staffers to keep:</Typography>
                <FormGroup>
                  {staff.map((staffer) => (
                    <FormControlLabel
                      key={staffer.id}
                      control={
                        <Checkbox
                          checked={selectedStaff.includes(staffer.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedStaff([...selectedStaff, staffer.id])
                            } else {
                              setSelectedStaff(selectedStaff.filter(id => id !== staffer.id))
                            }
                          }}
                        />
                      }
                      label={`${staffer.person?.firstName ?? ''} ${staffer.person?.lastName ?? ''} - ${staffer.title ?? ''}`}
                    />
                  ))}
                </FormGroup>
              </Grid>
            </Grid>
              )}
        </DialogContent>
      </Dialog>
      <AlertDialog
        onClose={() => { setOpenRetireDialog(false) }}
        open={openRetireDialog}
        onButtonClick={() => { void handleConfirmedRetirement() }}
        title='Member Retirement Confirmation'
        contentText='This action cannot be undone.'
        nameButtonCancel='Close'
        nameButtonConfirm='Retire'
        containedButton = {true}
      />
    </>
  )
}
